import React, { forwardRef } from 'react'

import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import { templateFormatter } from 'input-format'
import { useFormContext, Validate } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClearFieldIcon, LockLockedFillIcon, LockLockedIcon } from '@arcade/web/icons/react'
import { FieldLabel, IconInput, Input, PaddingInset, PasswordInput, spacing, Stack } from '@cash-design-system/react'
import {
  FormBlockerElementTextInputElement,
  FormBlockerElementTextInputElementInputField,
  FormBlockerElementTextInputElementInputFieldSecureFieldAccessory,
  FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory,
  FormBlockerElementTextInputElementKeyboardType,
  FormBlockerElementTextInputElementSecurity,
  FormBlockerElementTextInputElementValidation,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useCombinedInputRef } from '@cash-web/shared-feature-form'
import { FieldErrorText, getNestedValue, MaskedInput } from '@cash-web/shared-feature-form'
import { toPartialMatch } from '@cash-web/shared-util-validation'

import { ArcadeTextInputElement } from './ArcadeTextInputElement'

const getNumberFormatFromField = (validations?: FormBlockerElementTextInputElementValidation[]) => {
  const validationWithFormat = validations?.find(validation => validation.template?.template)

  if (
    validationWithFormat &&
    validationWithFormat.template?.template &&
    validationWithFormat.template?.template_placeholder_character
  ) {
    const { template, template_placeholder_character, user_insertable_format_characters } =
      validationWithFormat.template
    const mask = template
      .split('')
      .map(char => {
        if (char === template_placeholder_character || user_insertable_format_characters?.includes(char)) {
          return 'x'
        } else {
          return char
        }
      })
      .join('')

    return (value: string) => {
      const formattedText = templateFormatter(mask)(value).text

      if (validations?.some(validation => validation.template?.user_insertable_format_characters)) {
        const templateSplit = template?.split('')
        const splitFormattedText = formattedText.split('')

        // Check if the formatted text has the user insertable character
        const index = templateSplit.findIndex(char => user_insertable_format_characters?.includes(char))
        const hasUserInsertableChar = index && splitFormattedText[index] === user_insertable_format_characters

        // If no user insertable character is found, insert character to match template
        if (!hasUserInsertableChar && splitFormattedText.length >= index) {
          let shiftIndex = 0
          const updatedValueWithTemplate = templateSplit.reduce((acc, char, index) => {
            const currIndex = index - shiftIndex

            // Stop once the input is used up so nothing is inserted ahead of the caret
            if (currIndex >= value.length) {
              return acc
            }

            if (user_insertable_format_characters?.includes(char)) {
              acc += char
              if (!user_insertable_format_characters?.includes(value[currIndex])) {
                shiftIndex++
              }
            } else if (char === template_placeholder_character && value[currIndex]) {
              acc += value[currIndex]
            } else {
              acc += char
              shiftIndex++
            }

            return acc
          }, '')

          return updatedValueWithTemplate
        }
      }

      return formattedText
    }
  }

  return undefined
}

const ShowHideInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>((props, ref) => {
  const { t } = useTranslation()
  const showLabel = t('Show text', { context: 'button associated with secure text input to reveal masked text' })
  const hideLabel = t('Hide text', { context: 'button associated with secure text input to hide visible text' })
  return <PasswordInput {...props} showPasswordLabel={showLabel} hidePasswordLabel={hideLabel} />
})

const InputWithClearButton = forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>((props, ref) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext()
  const { combinedRef, inputRef } = useCombinedInputRef(ref)

  const handleClear = () => {
    const fieldName = props.name

    // This should never happen, but adding it here so the types behave below.
    if (!fieldName) {
      logger.error(new Error('InputWithClearButton: fieldName prop is required'))
      return
    }

    // Clear the value
    setValue(fieldName, '', {
      shouldValidate: false,
      shouldDirty: true,
    })

    // Focus input so user can immediately start typing.
    inputRef.current?.focus()
  }

  return (
    <IconInput
      {...props}
      type={'text'}
      icon={<ClearFieldIcon size={24} />}
      iconLabel={t('Clear input', { context: 'icon label on input' })}
      onIconClick={handleClear}
      ref={combinedRef}
    />
  )
})

const LockInput = forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>((props, ref) => {
  return <IconInput {...props} icon={<LockLockedFillIcon size={24} data-testid="lock-icon" />} ref={ref} />
})

const getInputComponent = (field: FormBlockerElementTextInputElementInputField) => {
  if (field.trailing_field_accessory === 'CLEAR_BUTTON') {
    return InputWithClearButton
  }

  if (field.security !== FormBlockerElementTextInputElementSecurity.SECURE) {
    return Input
  }

  if (field.trailing_field_accessory) {
    switch (field.trailing_field_accessory) {
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.SECURE_FIELD_SHOW_HIDE:
        return ShowHideInput
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.SECURE_FIELD_LOCK:
        return LockInput
      case FormBlockerElementTextInputElementInputFieldTrailingFieldAccessory.ABSENT:
        return Input
      default:
        // What to do if logged:
        // - Add support for the missing trailing_field_accessory.
        logger.warn(
          new Error(`TextInputElement: unsupported trailing_field_accessory member - ${field.trailing_field_accessory}`)
        )
    }
  }

  // This will be deprecated eventually, but not all server libraries have migrated to trailing_field_accessory.
  switch (field.secure_field_accessory) {
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.SHOW_HIDE:
      return ShowHideInput
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.LOCK:
      return LockInput
    case FormBlockerElementTextInputElementInputFieldSecureFieldAccessory.NONE:
      return Input
    default:
      // What to do if logged:
      // - Add support for the missing secure_field_accessory.
      logger.warn(
        new Error(`TextInputElement: unsupported secure_field_accessory member - ${field.secure_field_accessory}`)
      )
      return Input
  }
}

const SecurityText = styled.p`
  ${props => props.theme.typography.labelMedium}
  color: ${props => props.theme.colors.tertiaryLabel};
`

const StyledLockIcon = styled(LockLockedIcon)`
  height: 1.25rem;
  width: 1.25rem;
  margin-right: ${({ theme }) => theme.spacing.xxs};
  vertical-align: text-top;
`

type FieldInputProps = {
  name: string
  type: string
  value?: string
  autoFocus?: boolean
  field: FormBlockerElementTextInputElementInputField
  placeholder: string
  hasError: boolean
  validate: Validate<string>
  defaultValue?: string
  disabled?: boolean
}

const FieldInput = ({ field, hasError, name, validate, defaultValue, ...props }: FieldInputProps) => {
  const { register } = useFormContext()
  // add the start and end of string anchors to the regex otherwise partial match will always match.
  const validations = field.validations
    ?.filter(v => v.regex != null)
    ?.map(v => {
      try {
        return toPartialMatch(
          new RegExp(`${v.regex?.startsWith('^') ? '' : '^'}${v.regex}${v.regex?.endsWith('$') ? '' : '$'}`)
        )
      } catch {
        // in the event it fails to parse the regex, return null
        logger.error(new Error('Failed to create a partial match for regex:' + v.regex))
        return null
      }
    })

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (validations) {
      const input = event.target as HTMLInputElement

      // Skip validations if the user is holding a special key. They might be pasting, selecting
      // text, or otherwise performing actions we cannot validate against with onKeyPress
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return
      }

      let value = input.value
      // onKeyPress is not fired for special keys (Backspace, Shift, Alt), and is inconsistently
      // fired if modifiers are pressed with normal keys, but is fired for Enter.
      // This block prevents validating on [input value] + [Enter] when trying to submit the form.
      if (event.key !== 'Enter') {
        value = value + event.key
      }

      validations.forEach(regex => {
        if (regex == null) {
          return
        }
        // partial match will only return false if we do not partial match
        if (!regex.test(value)) {
          event.stopPropagation()
          event.preventDefault()
        }
      })
    }
  }
  const { ref, ...fieldProps } = register(name, { validate })
  const CustomInput = getInputComponent(field)

  if (field.keyboardType === FormBlockerElementTextInputElementKeyboardType.NUMBER_PAD) {
    const format = getNumberFormatFromField(field.validations)

    if (format) {
      return (
        <>
          <MaskedInput
            initialFormattedValue={defaultValue}
            CustomInput={CustomInput}
            name={name}
            format={format}
            isAllowed={values => {
              return Boolean(validations?.filter(regex => regex !== null).every(regex => regex.test(values.value)))
            }}
            readOnly={field.disabled}
            {...props}
            ref={ref}
          />
          <FieldErrorText name={name} />
        </>
      )
    }
  }

  return (
    <>
      <CustomInput
        className={hasError ? 'error' : ''}
        {...fieldProps}
        {...props}
        readOnly={field.disabled}
        defaultValue={defaultValue}
        onKeyPress={handleKeyPress}
        ref={ref}
      />
      <FieldErrorText name={name} />
    </>
  )
}

interface Props extends FormBlockerElementTextInputElement {
  formSectionIndex: number
  elementId?: string
  shouldAutoFocus?: boolean
}
const placeholderReplacement: Record<string, string> = {
  'Authentication code': 'Code',
  'Confirmation code': 'Code',
}

export function getInputFieldKey(formSectionIndex: number, inputFieldIndex: number) {
  return `${formSectionIndex}.text_input_result.input_field_values.${inputFieldIndex}`
}

const customErrorMessages = (elementId: string | undefined, t: TFunction, hint_text?: string) => {
  switch (elementId) {
    case 'EMPLOYER_NAME_INPUT_ID':
      return t('Enter the name of your employer.', { context: 'modal error message under input field' })
    case 'CAPTURE_PHONE_INPUT':
      return '' // to match mobile
    default:
      return t('Invalid {field}', {
        context: 'modal error message under input field',
        field: (hint_text || 'field').toLowerCase(),
      })
  }
}

const escapeRegexCharacters = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const normalizeServerRegex = (regex: string) =>
  regex.replace(/\\Q([\s\S]*?)\\E/g, (_match, value: string) => escapeRegexCharacters(value))

const testRegex = (regex: string | undefined, value: string) => {
  if (regex === undefined) {
    return false
  }

  if (regex === '') {
    return true
  }

  try {
    return RegExp(normalizeServerRegex(regex)).test(value)
  } catch {
    logger.error(new Error('Failed to evaluate regex: ' + regex))
    return false
  }
}

const getValidationErrorMessage = ({
  elementId,
  field,
  hintText,
  t,
  value,
}: {
  elementId: string | undefined
  field: FormBlockerElementTextInputElementInputField
  hintText?: string
  t: TFunction
  value: string
}) => {
  const invalidInput = field.invalid_inputs?.find(input => testRegex(input.regex, value))

  if (invalidInput) {
    return invalidInput.message ?? customErrorMessages(elementId, t, hintText)
  }

  const invalidValidation = field.validations?.find(
    validation => validation.regex && !testRegex(validation.regex, value)
  )

  if (invalidValidation) {
    return customErrorMessages(elementId, t, hintText)
  }

  return true
}

export function TextInputElement({
  input_fields,
  elementId,
  formSectionIndex,
  security_text,
  shouldAutoFocus,
}: Props): React.ReactElement | null {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeTextInputElement
        input_fields={input_fields}
        elementId={elementId}
        formSectionIndex={formSectionIndex}
        security_text={security_text}
        shouldAutoFocus={shouldAutoFocus}
      />
    )
  }

  if (!input_fields) {
    return null
  }
  return (
    <div>
      <input type="hidden" value={elementId} {...register(`${formSectionIndex}.id`)} />

      <Stack space={spacing.m}>
        {input_fields.map((field, index) => {
          const isLastField = index === input_fields.length - 1
          const key = getInputFieldKey(formSectionIndex, index)
          const hasError = getNestedValue([formSectionIndex, 'text_input_result', 'input_field_values', index], errors)

          const { hint_text, keyboardType, label_text, prefill_text, security } = field

          let type = 'text'
          if (security === FormBlockerElementTextInputElementSecurity.SECURE) {
            type = 'password'
          } else if (keyboardType === FormBlockerElementTextInputElementKeyboardType.EMAIL) {
            type = 'email'
          } else if (keyboardType === FormBlockerElementTextInputElementKeyboardType.NUMBER_PAD) {
            // if we change this to number, we only have an up/down arrow to increment/decrement
            type = 'text'
          }

          const shouldIncludeDisabledFieldValueInFormSubmission = Boolean(field.disabled && prefill_text)

          return (
            <div key={key}>
              <FieldLabel label={label_text ?? hint_text ?? ''}>
                <FieldInput
                  autoFocus={shouldAutoFocus && index === 0}
                  name={key}
                  field={field}
                  disabled={field.disabled}
                  placeholder={hint_text != null ? (placeholderReplacement[hint_text] ?? hint_text) : ''}
                  type={type}
                  defaultValue={prefill_text}
                  hasError={!!hasError}
                  validate={value => getValidationErrorMessage({ elementId, field, hintText: hint_text, t, value })}
                  aria-describedby={security_text ? `${elementId}-security-text` : undefined}
                />
              </FieldLabel>
              {shouldIncludeDisabledFieldValueInFormSubmission && (
                <input type="hidden" defaultValue={prefill_text} {...register(key)} />
              )}
              {security_text && !hasError && isLastField && (
                <PaddingInset>
                  <SecurityText id={`${elementId}-security-text`}>
                    <StyledLockIcon />
                    {security_text}
                  </SecurityText>
                </PaddingInset>
              )}
            </div>
          )
        })}
      </Stack>
    </div>
  )
}
